import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { formatarDataBR, hojeISO } from '../common/data';

interface Turma { id: number; nome: string; }

interface ItemDespensa {
  id: number; nome: string; unidade: string; categoria: string;
  estoque_minimo: number; saldo: number; totalEntradas: number; totalSaidas: number;
  totalPerdas: number; negativo: boolean; taxaPerdaPct: number | null;
  abaixoDoMinimo: boolean; movimentos: number;
}

interface AlertasDespensa {
  hoje: string; diasDeAviso: number;
  vencidos: any[]; vencendo: any[]; abaixoDoMinimo: any[]; saldoNegativo: any[];
}

interface LinhaAceitacao {
  id_aluno: number; nome: string; nivel: string | null; observacao: string; registrado: boolean;
  /** Substituições que a creche aprovou para esta criança nesta refeição. */
  substituicoes: string[];
}

interface ResumoCrianca {
  id_aluno: number; nome: string; registros: number; indice: number | null;
  aceitouTudo: number; aceitouParte: number; aceitouPouco: number; recusou: number; acompanhar: boolean;
}

interface Alternativa {
  id: number; id_aluno: number; crianca: string; refeicao: string | null;
  alternativa: string; motivo: string | null;
  status: 'pendente' | 'aceita' | 'recusada'; resposta: string | null;
  criado_em: string; respondido_em: string | null;
}

const REFEICOES = [
  { valor: 'cafe_manha', label: 'Café da manhã' },
  { valor: 'lanche_manha', label: 'Lanche da manhã' },
  { valor: 'almoco', label: 'Almoço' },
  { valor: 'lanche_tarde', label: 'Lanche da tarde' },
  { valor: 'jantar', label: 'Jantar' },
];

const NIVEIS = [
  { valor: 'aceitou_tudo', label: 'Aceitou tudo', icone: '🍽️', cor: '#2e7d32' },
  { valor: 'aceitou_parte', label: 'Aceitou boa parte', icone: '🥄', cor: '#558b2f' },
  { valor: 'aceitou_pouco', label: 'Comeu pouco', icone: '🥲', cor: '#e65100' },
  { valor: 'recusou', label: 'Recusou', icone: '🚫', cor: '#c62828' },
];

const UNIDADES = ['kg', 'g', 'l', 'ml', 'unidade', 'pacote', 'lata', 'caixa'];
const CATEGORIAS = [
  ['hortifruti', 'Hortifrúti'], ['proteina', 'Proteína'], ['graos_cereais', 'Grãos e cereais'],
  ['laticinios', 'Laticínios'], ['mercearia', 'Mercearia'], ['bebidas', 'Bebidas'],
  ['formulas_infantis', 'Fórmulas infantis'], ['descartaveis', 'Descartáveis'], ['outros', 'Outros'],
];

const primeiroDiaDoMes = () => hojeISO().slice(0, 8) + '01';

const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 20,
};

export default function NutricaoPage() {
  const [aba, setAba] = useState<'despensa' | 'aceitacao' | 'propostas'>('despensa');
  const [turmas, setTurmas] = useState<Turma[]>([]);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');

  // ── Despensa ──
  const [itens, setItens] = useState<ItemDespensa[]>([]);
  const [alertas, setAlertas] = useState<AlertasDespensa | null>(null);
  const [novoItem, setNovoItem] = useState({ nome: '', unidade: 'kg', categoria: 'outros', estoque_minimo: 0 });
  const [mov, setMov] = useState({
    id_item: 0, tipo: 'entrada', quantidade: '', data: hojeISO(), lote: '', valido_ate: '', motivo: '',
  });
  const [salvandoMov, setSalvandoMov] = useState(false);

  // ── Aceitação ──
  const [idTurma, setIdTurma] = useState(0);
  const [data, setData] = useState(hojeISO());
  const [refeicao, setRefeicao] = useState('almoco');
  const [folha, setFolha] = useState<LinhaAceitacao[]>([]);
  const [cardapioPrevisto, setCardapioPrevisto] = useState<string | null>(null);
  const [carregandoFolha, setCarregandoFolha] = useState(false);
  const [salvandoFolha, setSalvandoFolha] = useState(false);
  const [alertasAceitacao, setAlertasAceitacao] = useState<{ total: number; criancas: ResumoCrianca[]; minimoRefeicoesConsiderado: number } | null>(null);

  // ── Propostas da família ──
  const [propostas, setPropostas] = useState<{ total: number; pendentes: number; aguardandoDesde: string | null; alternativas: Alternativa[] } | null>(null);
  const [respostas, setRespostas] = useState<Record<number, string>>({});
  const [respondendo, setRespondendo] = useState(0);

  useEffect(() => {
    api.get<Turma[]>('/turmas').then(({ data }) => {
      const lista = Array.isArray(data) ? data : [];
      setTurmas(lista);
      if (lista.length === 1) setIdTurma(lista[0].id);
    }).catch(() => {});
  }, []);

  const carregarDespensa = useCallback(async () => {
    try {
      const [{ data: lista }, { data: al }] = await Promise.all([
        api.get('/nutricao/itens'),
        api.get('/nutricao/estoque/alertas'),
      ]);
      setItens(lista.itens || []);
      setAlertas(al);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar a despensa.');
    }
  }, []);

  useEffect(() => { carregarDespensa(); }, [carregarDespensa]);

  useEffect(() => {
    api.get(`/nutricao/aceitacao/alertas?inicio=${primeiroDiaDoMes()}&fim=${hojeISO()}`)
      .then(({ data }) => setAlertasAceitacao(data))
      .catch(() => setAlertasAceitacao(null));
  }, []);

  const carregarPropostas = useCallback(async () => {
    try {
      const { data } = await api.get('/nutricao/alternativas');
      setPropostas(data);
    } catch { setPropostas(null); }
  }, []);

  // Carrega já na abertura da tela, não só ao entrar na aba: o contador de
  // pendências precisa aparecer antes de alguém pensar em clicar.
  useEffect(() => { carregarPropostas(); }, [carregarPropostas]);

  const responder = async (id: number, status: 'aceita' | 'recusada') => {
    const resposta = (respostas[id] || '').trim();
    if (status === 'recusada' && !resposta) {
      setErro('Explique o motivo da recusa — a família precisa saber por que não é possível.');
      return;
    }
    setRespondendo(id); setErro(''); setMsg('');
    try {
      await api.put(`/nutricao/alternativas/${id}`, { status, resposta });
      setMsg(status === 'aceita'
        ? '✅ Proposta aceita — a substituição já aparece na folha de aceitação da turma.'
        : '✅ Recusa registrada com a justificativa; a família vê a resposta no portal.');
      setRespostas((r) => ({ ...r, [id]: '' }));
      carregarPropostas();
      setTimeout(() => setMsg(''), 5000);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível registrar a resposta.');
    } finally { setRespondendo(0); }
  };

  /** Há quantos dias a proposta espera — é o que dá urgência ao número. */
  const diasEsperando = (iso: string) => {
    const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return dias <= 0 ? 'hoje' : dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
  };

  const criarItem = async () => {
    if (!novoItem.nome.trim()) { setErro('Informe o nome do item.'); return; }
    setErro(''); setMsg('');
    try {
      await api.post('/nutricao/itens', novoItem);
      setNovoItem({ nome: '', unidade: 'kg', categoria: 'outros', estoque_minimo: 0 });
      setMsg('✅ Item cadastrado.');
      carregarDespensa();
      setTimeout(() => setMsg(''), 3000);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Falha ao cadastrar o item.');
    }
  };

  const registrarMovimento = async () => {
    if (!mov.id_item) { setErro('Selecione o item.'); return; }
    if (!mov.quantidade || Number(mov.quantidade) <= 0) { setErro('Informe uma quantidade maior que zero.'); return; }
    setSalvandoMov(true); setErro(''); setMsg('');
    try {
      const { data: resp } = await api.post('/nutricao/estoque/movimentos', {
        ...mov,
        quantidade: Number(mov.quantidade),
        lote: mov.lote || null,
        valido_ate: mov.valido_ate || null,
        motivo: mov.motivo || null,
      });
      setMsg(`✅ Movimento registrado. Saldo do item: ${resp.saldo}.`);
      // O alerta de saldo negativo vem do servidor e é MOSTRADO, não engolido:
      // é o sinal de que faltou lançar uma entrada.
      if (resp.alerta) setErro(resp.alerta);
      setMov({ ...mov, quantidade: '', lote: '', valido_ate: '', motivo: '' });
      carregarDespensa();
      setTimeout(() => setMsg(''), 4000);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Falha ao registrar o movimento.');
    } finally { setSalvandoMov(false); }
  };

  const carregarFolha = useCallback(async () => {
    if (!idTurma || !data || !refeicao) { setFolha([]); return; }
    setCarregandoFolha(true); setErro(''); setMsg('');
    try {
      const { data: resp } = await api.get(
        `/nutricao/aceitacao/folha?data=${data}&id_turma=${idTurma}&refeicao=${refeicao}`,
      );
      setFolha(resp.criancas || []);
      setCardapioPrevisto(resp.cardapioPrevisto || null);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar a folha de aceitação.');
    } finally { setCarregandoFolha(false); }
  }, [idTurma, data, refeicao]);

  useEffect(() => { if (aba === 'aceitacao') carregarFolha(); }, [aba, carregarFolha]);

  const setNivel = (idAluno: number, nivel: string) =>
    setFolha((f) => f.map((i) => (i.id_aluno === idAluno ? { ...i, nivel } : i)));
  const setObservacao = (idAluno: number, v: string) =>
    setFolha((f) => f.map((i) => (i.id_aluno === idAluno ? { ...i, observacao: v } : i)));

  /**
   * Marca a turma inteira num nível e deixa a educadora ajustar as exceções.
   *
   * Sem isto, registrar aceitação seriam 20 toques por refeição, 5 vezes ao dia
   * — e o que não cabe na rotina simplesmente não é preenchido. Só preenche
   * quem ainda não foi marcado, para não apagar o que já se anotou à mão.
   */
  const marcarTodos = (nivel: string) =>
    setFolha((f) => f.map((i) => ({ ...i, nivel: i.nivel ?? nivel })));

  const salvarFolha = async () => {
    const registros = folha.filter((i) => i.nivel).map((i) => ({
      id_aluno: i.id_aluno, nivel: i.nivel, observacao: i.observacao,
    }));
    if (!registros.length) { setErro('Marque ao menos uma criança antes de salvar.'); return; }
    setSalvandoFolha(true); setErro(''); setMsg('');
    try {
      const { data: resp } = await api.post('/nutricao/aceitacao', {
        data, id_turma: idTurma, refeicao, registros,
      });
      setMsg(`✅ Aceitação salva — ${resp.gravados} registro(s).`);
      if (resp.ignorados?.length) {
        setErro(`${resp.ignorados.length} criança(s) não pertencem mais a esta turma e foram ignoradas. Recarregue a folha.`);
      }
      carregarFolha();
      setTimeout(() => setMsg(''), 4000);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Falha ao salvar a aceitação.');
    } finally { setSalvandoFolha(false); }
  };

  const corIndice = (i: number | null) => (i === null ? '#888' : i >= 66 ? '#2e7d32' : i >= 40 ? '#e65100' : '#c62828');

  const totalAlertasDespensa = alertas
    ? alertas.vencidos.length + alertas.vencendo.length + alertas.abaixoDoMinimo.length + alertas.saldoNegativo.length
    : 0;

  return (
    <div className="stack-cadastros" style={{ maxWidth: 1000 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['despensa', 'aceitacao', 'propostas'] as const).map((a) => (
          <button key={a} onClick={() => setAba(a)}
            style={{
              padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 700,
              border: aba === a ? '2px solid #0f766e' : '1px solid #ddd',
              background: aba === a ? '#0f766e' : '#fff', color: aba === a ? '#fff' : '#555',
            }}>
            {a === 'despensa'
              ? `🥫 Despensa${totalAlertasDespensa ? ` (${totalAlertasDespensa})` : ''}`
              : a === 'aceitacao'
                ? '🍽️ Aceitação alimentar'
                : `👪 Propostas das famílias${propostas?.pendentes ? ` (${propostas.pendentes})` : ''}`}
          </button>
        ))}
      </div>

      {erro && <div style={{ color: '#c62828', marginBottom: 10 }}>{erro}</div>}
      {msg && <div style={{ color: '#2e7d32', marginBottom: 10 }}>{msg}</div>}

      {aba === 'despensa' && (
        <>
          {/* ── ALERTAS DA DESPENSA ──
              Primeiro de propósito: é o que exige ação hoje. */}
          {alertas && totalAlertasDespensa > 0 && (
            <section style={{ ...card, background: '#fff4f4', border: '1px solid #f3c9c9' }}>
              <h3 style={{ marginTop: 0, color: '#c62828' }}>⚠️ A despensa pede atenção</h3>

              {alertas.vencidos.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <strong style={{ color: '#c62828' }}>Vencidos ({alertas.vencidos.length})</strong>
                  {alertas.vencidos.map((v, i) => (
                    <div key={i} style={{ fontSize: 14, padding: '4px 0' }}>
                      {v.nome} — lote {v.lote || 'sem lote'} · venceu em {formatarDataBR(v.valido_ate)} · saldo {v.saldo} {v.unidade}
                      {v.conferirSaldo && <em style={{ color: '#8a6d6d' }}> (conferir: houve baixa sem lote informado)</em>}
                    </div>
                  ))}
                </div>
              )}

              {alertas.vencendo.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <strong style={{ color: '#e65100' }}>Vencem em até {alertas.diasDeAviso} dias ({alertas.vencendo.length})</strong>
                  {alertas.vencendo.map((v, i) => (
                    <div key={i} style={{ fontSize: 14, padding: '4px 0' }}>
                      {v.nome} — lote {v.lote || 'sem lote'} · até {formatarDataBR(v.valido_ate)} · saldo {v.saldo} {v.unidade}
                    </div>
                  ))}
                </div>
              )}

              {alertas.abaixoDoMinimo.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <strong style={{ color: '#1565c0' }}>Abaixo do mínimo — lista de compras ({alertas.abaixoDoMinimo.length})</strong>
                  {alertas.abaixoDoMinimo.map((v, i) => (
                    <div key={i} style={{ fontSize: 14, padding: '4px 0' }}>
                      {v.nome} — tem {v.saldo} {v.unidade}, mínimo {v.estoque_minimo} · <strong>faltam {v.faltam} {v.unidade}</strong>
                    </div>
                  ))}
                </div>
              )}

              {alertas.saldoNegativo.length > 0 && (
                <div>
                  <strong style={{ color: '#6a1b9a' }}>Saldo negativo ({alertas.saldoNegativo.length})</strong>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: '#7a4a4a' }}>
                    Saiu mais do que consta ter entrado — quase sempre é uma compra que ninguém lançou.
                  </p>
                  {alertas.saldoNegativo.map((v, i) => (
                    <div key={i} style={{ fontSize: 14, padding: '4px 0' }}>{v.nome} — saldo {v.saldo}</div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ── REGISTRAR MOVIMENTO ── */}
          <section style={card}>
            <h3 style={{ marginTop: 0 }}>📦 Registrar entrada, saída ou perda</h3>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
              <label>Item
                <select value={mov.id_item} onChange={(e) => setMov({ ...mov, id_item: Number(e.target.value) })} style={{ display: 'block' }}>
                  <option value={0}>Selecione...</option>
                  {itens.map((i) => <option key={i.id} value={i.id}>{i.nome} ({i.unidade})</option>)}
                </select>
              </label>
              <label>Tipo
                <select value={mov.tipo} onChange={(e) => setMov({ ...mov, tipo: e.target.value })} style={{ display: 'block' }}>
                  <option value="entrada">Entrada (compra/doação)</option>
                  <option value="saida">Saída (foi para a cozinha)</option>
                  <option value="perda">Perda (vencido/avaria/sobra)</option>
                </select>
              </label>
              <label>Quantidade
                <input type="number" min="0" step="0.001" value={mov.quantidade}
                  onChange={(e) => setMov({ ...mov, quantidade: e.target.value })} style={{ display: 'block', width: 110 }} />
              </label>
              <label>Data
                <input type="date" value={mov.data} onChange={(e) => setMov({ ...mov, data: e.target.value })} style={{ display: 'block' }} />
              </label>
              <label>Lote
                <input value={mov.lote} onChange={(e) => setMov({ ...mov, lote: e.target.value })}
                  placeholder="opcional" style={{ display: 'block', width: 110 }} />
              </label>
              {mov.tipo === 'entrada' && (
                <label>Validade
                  <input type="date" value={mov.valido_ate} onChange={(e) => setMov({ ...mov, valido_ate: e.target.value })} style={{ display: 'block' }} />
                </label>
              )}
              <label>{mov.tipo === 'perda' ? 'Motivo (obrigatório)' : 'Observação'}
                <input value={mov.motivo} onChange={(e) => setMov({ ...mov, motivo: e.target.value })}
                  placeholder={mov.tipo === 'perda' ? 'vencido, avaria, sobra…' : 'nota fiscal, fornecedor…'}
                  style={{ display: 'block', width: 190 }} />
              </label>
              <button onClick={registrarMovimento} disabled={salvandoMov}
                style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: salvandoMov ? '#9cc7a2' : '#2e7d32', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                {salvandoMov ? 'Salvando…' : 'Registrar'}
              </button>
            </div>
            {mov.tipo === 'entrada' && (
              <p style={{ margin: '10px 0 0', fontSize: 12.5, color: '#666' }}>
                Informe lote e validade na entrada: é o que permite o sistema avisar antes de o alimento vencer.
              </p>
            )}
          </section>

          {/* ── ITENS COM SALDO ── */}
          <section style={card}>
            <h3 style={{ marginTop: 0 }}>🥫 Itens da despensa</h3>
            {itens.length === 0 ? (
              <div style={{ color: '#888', marginBottom: 12 }}>Nenhum item cadastrado ainda.</div>
            ) : (
              <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 8, marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead><tr style={{ background: '#fafafa', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px' }}>Item</th>
                    <th style={{ padding: '8px 12px' }}>Categoria</th>
                    <th style={{ padding: '8px 12px' }}>Saldo</th>
                    <th style={{ padding: '8px 12px' }}>Mínimo</th>
                    <th style={{ padding: '8px 12px' }}>Entradas</th>
                    <th style={{ padding: '8px 12px' }}>Saídas</th>
                    <th style={{ padding: '8px 12px' }}>Perdas</th>
                    <th style={{ padding: '8px 12px' }}>% perda</th>
                  </tr></thead>
                  <tbody>
                    {itens.map((i) => (
                      <tr key={i.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 600 }}>{i.nome}</td>
                        <td style={{ padding: '8px 12px' }}>{CATEGORIAS.find((c) => c[0] === i.categoria)?.[1] || i.categoria}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 700, color: i.negativo ? '#6a1b9a' : i.abaixoDoMinimo ? '#c62828' : '#2e7d32' }}>
                          {i.saldo} {i.unidade}
                        </td>
                        <td style={{ padding: '8px 12px', color: '#888' }}>{i.estoque_minimo || '—'}</td>
                        <td style={{ padding: '8px 12px' }}>{i.totalEntradas}</td>
                        <td style={{ padding: '8px 12px' }}>{i.totalSaidas}</td>
                        <td style={{ padding: '8px 12px', color: i.totalPerdas > 0 ? '#e65100' : '#333' }}>{i.totalPerdas}</td>
                        {/* null = nunca entrou nada, então não há como calcular. "—", nunca 0%. */}
                        <td style={{ padding: '8px 12px' }}>{i.taxaPerdaPct === null ? '—' : `${i.taxaPerdaPct}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end', borderTop: '1px solid #f0f0f0', paddingTop: 14 }}>
              <label>Novo item
                <input value={novoItem.nome} onChange={(e) => setNovoItem({ ...novoItem, nome: e.target.value })}
                  placeholder="Arroz, leite integral…" style={{ display: 'block', width: 200 }} />
              </label>
              <label>Unidade
                <select value={novoItem.unidade} onChange={(e) => setNovoItem({ ...novoItem, unidade: e.target.value })} style={{ display: 'block' }}>
                  {UNIDADES.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </label>
              <label>Categoria
                <select value={novoItem.categoria} onChange={(e) => setNovoItem({ ...novoItem, categoria: e.target.value })} style={{ display: 'block' }}>
                  {CATEGORIAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <label>Estoque mínimo
                <input type="number" min="0" step="0.001" value={novoItem.estoque_minimo}
                  onChange={(e) => setNovoItem({ ...novoItem, estoque_minimo: Number(e.target.value) })}
                  style={{ display: 'block', width: 110 }} />
              </label>
              <button onClick={criarItem}
                style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #cfe8d4', background: '#eaf6ec', cursor: 'pointer', fontWeight: 600 }}>
                + Cadastrar item
              </button>
            </div>
          </section>
        </>
      )}

      {aba === 'aceitacao' && (
        <>
          {/* ── ALERTA NUTRICIONAL ── */}
          {alertasAceitacao && alertasAceitacao.total > 0 && (
            <section style={{ ...card, background: '#fff4f4', border: '1px solid #f3c9c9' }}>
              <h3 style={{ marginTop: 0, color: '#c62828' }}>
                ⚠️ {alertasAceitacao.total} criança(s) comendo pouco neste mês
              </h3>
              <p style={{ margin: '0 0 12px', color: '#7a4a4a', fontSize: 14 }}>
                Recusa persistente costuma ter causa — dentição, alteração de rotina, saúde.
                Da menor aceitação para a maior:
              </p>
              {alertasAceitacao.criancas.map((c) => (
                <div key={c.id_aluno} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#fff', border: '1px solid #f0dcdc', borderRadius: 8, padding: '8px 12px', marginBottom: 6 }}>
                  <span style={{ flex: '1 1 200px', fontWeight: 600 }}>{c.nome}</span>
                  <span style={{ fontWeight: 800, color: corIndice(c.indice), minWidth: 62 }}>{c.indice}%</span>
                  <span style={{ fontSize: 13, color: '#666' }}>
                    {c.recusou} recusa(s) · {c.aceitouPouco} vez(es) comeu pouco · em {c.registros} refeição(ões)
                  </span>
                </div>
              ))}
              <p style={{ margin: '12px 0 0', color: '#8a6d6d', fontSize: 12.5 }}>
                Só entram crianças com pelo menos {alertasAceitacao.minimoRefeicoesConsiderado} refeições registradas —
                antes disso o índice ainda não significa nada.
              </p>
            </section>
          )}

          {/* ── FOLHA DE ACEITAÇÃO ── */}
          <section style={card}>
            <h3 style={{ marginTop: 0 }}>🍽️ Registro de aceitação</h3>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end', marginBottom: 12 }}>
              <label>Turma
                <select value={idTurma} onChange={(e) => setIdTurma(Number(e.target.value))} style={{ display: 'block' }}>
                  <option value={0}>Selecione...</option>
                  {turmas.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </select>
              </label>
              <label>Data
                <input type="date" value={data} onChange={(e) => setData(e.target.value)} style={{ display: 'block' }} />
              </label>
              <label>Refeição
                <select value={refeicao} onChange={(e) => setRefeicao(e.target.value)} style={{ display: 'block' }}>
                  {REFEICOES.map((r) => <option key={r.valor} value={r.valor}>{r.label}</option>)}
                </select>
              </label>
              {folha.length > 0 && (
                <button onClick={() => marcarTodos('aceitou_tudo')}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cfe8d4', background: '#eaf6ec', cursor: 'pointer' }}>
                  🍽️ Todos aceitaram
                </button>
              )}
            </div>

            {cardapioPrevisto && (
              <div style={{ background: '#f0fdfa', border: '1px solid #ccfbf1', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 14 }}>
                <strong>Previsto no cardápio:</strong> {cardapioPrevisto}
              </div>
            )}

            {carregandoFolha ? <div>Carregando…</div> : !idTurma ? (
              <div style={{ color: '#888' }}>Selecione uma turma para registrar a aceitação.</div>
            ) : folha.length === 0 ? (
              <div style={{ color: '#888' }}>Nenhuma criança ativa nesta turma.</div>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {folha.map((i) => (
                    <div key={i.id_aluno} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 10px', border: '1px solid #f0f0f0', borderRadius: 8 }}>
                      <span style={{ flex: '1 1 180px', fontWeight: 600 }}>
                        {i.nome}
                        {/* A substituição aprovada aparece AQUI porque é aqui que a
                            educadora está na hora de servir. Decisão que fica só
                            no registro de aprovação não muda prato nenhum. */}
                        {i.substituicoes?.length > 0 && (
                          <span style={{ display: 'block', fontSize: 12, color: '#0f766e', fontWeight: 600, marginTop: 2 }}>
                            🔄 Servir no lugar: {i.substituicoes.join(' · ')}
                          </span>
                        )}
                      </span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {NIVEIS.map((n) => (
                          <button key={n.valor} onClick={() => setNivel(i.id_aluno, n.valor)} title={n.label}
                            style={{
                              padding: '5px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                              border: i.nivel === n.valor ? `2px solid ${n.cor}` : '1px solid #ddd',
                              background: i.nivel === n.valor ? n.cor : '#fff',
                              color: i.nivel === n.valor ? '#fff' : '#555',
                            }}>
                            {n.icone}
                          </button>
                        ))}
                      </div>
                      {(i.nivel === 'aceitou_pouco' || i.nivel === 'recusou') && (
                        <input placeholder="O que aconteceu? (opcional)" value={i.observacao}
                          onChange={(e) => setObservacao(i.id_aluno, e.target.value)} style={{ flex: '1 1 200px' }} />
                      )}
                      {/* Sem marcação nenhuma a criança fica FORA da conta — não vira recusa. */}
                      {!i.nivel && <span style={{ fontSize: 12.5, color: '#999' }}>não registrado</span>}
                    </div>
                  ))}
                </div>
                <button onClick={salvarFolha} disabled={salvandoFolha}
                  style={{ marginTop: 14, padding: '10px 20px', borderRadius: 8, border: 'none', background: salvandoFolha ? '#9cc7a2' : '#2e7d32', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                  {salvandoFolha ? 'Salvando…' : '💾 Salvar aceitação'}
                </button>
                <p style={{ margin: '10px 0 0', fontSize: 12.5, color: '#666' }}>
                  Criança sem marcação não é contada como recusa — ela simplesmente fica fora do índice.
                </p>
              </>
            )}
          </section>
        </>
      )}

      {aba === 'propostas' && (
        <section style={card}>
          <h3 style={{ marginTop: 0 }}>👪 Alternativas propostas pelas famílias</h3>
          <p style={{ margin: '0 0 12px', fontSize: 13.5, color: '#64748b' }}>
            Só famílias de crianças com restrição alimentar registrada podem propor.
            Aceitar registra a substituição na criança e ela passa a aparecer na folha de aceitação
            da turma, para quem serve o prato. <strong>Recusar exige justificativa</strong> — a
            família lê a resposta no portal.
          </p>

          {!propostas || propostas.total === 0 ? (
            <div style={{ color: '#888' }}>Nenhuma proposta recebida até agora.</div>
          ) : (
            <>
              {propostas.pendentes > 0 && (
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 14 }}>
                  ⏳ <strong>{propostas.pendentes}</strong> proposta(s) aguardando resposta
                  {propostas.aguardandoDesde && <> — a mais antiga {diasEsperando(propostas.aguardandoDesde)}</>}
                </div>
              )}

              {propostas.alternativas.map((p) => (
                <div key={p.id} style={{
                  border: '1px solid #f0f0f0',
                  borderLeft: `4px solid ${p.status === 'aceita' ? '#2e7d32' : p.status === 'recusada' ? '#c62828' : '#e65100'}`,
                  borderRadius: 8, padding: 12, marginBottom: 10,
                }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <strong>{p.crianca}</strong>
                    <span style={{ fontSize: 12.5, color: '#64748b' }}>
                      {p.refeicao ? REFEICOES.find((r) => r.valor === p.refeicao)?.label : 'Todas as refeições'}
                    </span>
                    <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#64748b' }}>
                      {p.status === 'pendente' ? `aguardando ${diasEsperando(p.criado_em)}`
                        : p.status === 'aceita' ? '✅ aceita' : '❌ recusada'}
                    </span>
                  </div>

                  <div style={{ marginTop: 6, fontSize: 14 }}><strong>Propõe:</strong> {p.alternativa}</div>
                  {p.motivo && <div style={{ fontSize: 13.5, color: '#475569' }}><strong>Motivo:</strong> {p.motivo}</div>}
                  {p.resposta && (
                    <div style={{ fontSize: 13.5, color: '#475569', marginTop: 4 }}>
                      <strong>Sua resposta:</strong> {p.resposta}
                    </div>
                  )}

                  {p.status === 'pendente' && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
                      <input
                        value={respostas[p.id] || ''}
                        onChange={(e) => setRespostas((r) => ({ ...r, [p.id]: e.target.value }))}
                        placeholder="Resposta à família (obrigatória para recusar)"
                        style={{ flex: '1 1 280px' }}
                      />
                      <button onClick={() => responder(p.id, 'aceita')} disabled={respondendo === p.id}
                        style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#2e7d32', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                        Aceitar
                      </button>
                      <button onClick={() => responder(p.id, 'recusada')} disabled={respondendo === p.id}
                        style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #c62828', background: '#fff', color: '#c62828', fontWeight: 700, cursor: 'pointer' }}>
                        Recusar
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </section>
      )}
    </div>
  );
}
